import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const Logo = ({ onClick, invert = false }) => (
  <a
    href="#home"
    onClick={onClick}
    data-testid="nav-logo"
    className="select-none inline-flex items-center"
  >
    <img
      src="/logo.png"
      alt="HM Sport Consulting"
      className={`h-20 md:h-24 w-auto object-contain ${invert ? "bg-white p-1" : ""}`}
    />
  </a>
);

const LangSwitch = ({ className = "", dark = false }) => {
  const { lang, setLang } = useLang();
  const base = "text-xs uppercase tracking-[0.2em] font-bold transition-colors duration-200";
  const activeCls = dark ? "text-white" : "text-black";
  const idleCls = dark ? "text-white/40 hover:text-white" : "text-neutral-400 hover:text-black";
  return (
    <div className={`flex items-center gap-2.5 ${className}`} data-testid="lang-switch">
      <button
        onClick={() => setLang("fr")}
        data-testid="lang-fr"
        className={`${base} flex items-center gap-1.5 ${lang === "fr" ? activeCls : idleCls}`}
      >
        <img
          src="https://flagcdn.com/w40/fr.png"
          srcSet="https://flagcdn.com/w80/fr.png 2x"
          alt="Drapeau France"
          className={`w-5 h-auto object-contain transition-opacity duration-200 ${
            lang === "fr" ? "opacity-100" : "opacity-50"
          }`}
        />
        FR
      </button>
      <span className={dark ? "text-white/30" : "text-neutral-300"}>/</span>
      <button
        onClick={() => setLang("en")}
        data-testid="lang-en"
        className={`${base} flex items-center gap-1.5 ${lang === "en" ? activeCls : idleCls}`}
      >
        <img
          src="https://flagcdn.com/w40/gb.png"
          srcSet="https://flagcdn.com/w80/gb.png 2x"
          alt="United Kingdom flag"
          className={`w-5 h-auto object-contain transition-opacity duration-200 ${
            lang === "en" ? "opacity-100" : "opacity-50"
          }`}
        />
        EN
      </button>
    </div>
  );
};

export const Nav = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { label: t.nav.home, href: "#home" },
    { label: t.nav.agency, href: "#agence" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.players, href: "#joueurs" },
    { label: t.nav.contact, href: "#contact" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      <motion.header
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 border-b border-black transition-shadow duration-500 bg-white ${
          scrolled ? "shadow-sm" : ""
        }`}
        data-testid="main-nav"
      >
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 py-4">
          <div className="relative flex items-center justify-center">
            <div className="absolute left-0 top-1/2 -translate-y-1/2">
              <LangSwitch />
            </div>

            <Logo onClick={(e) => go(e, "#home")} />

            <a
              href="#contact"
              onClick={(e) => go(e, "#contact")}
              data-testid="nav-cta"
              className="absolute right-0 top-1/2 -translate-y-1/2 hidden md:inline-flex items-center px-6 py-3 bg-black text-white text-xs uppercase tracking-[0.2em] font-bold border border-black transition-colors duration-300 hover:bg-white hover:text-black"
            >
              {t.nav.cta}
            </a>

            <button
              className="absolute right-0 top-1/2 -translate-y-1/2 text-black md:hidden"
              onClick={() => setOpen(true)}
              data-testid="nav-open-menu"
              aria-label="Menu"
            >
              <Menu size={26} />
            </button>
          </div>

          <nav className="hidden md:flex items-center justify-center gap-9 mt-3">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                className="text-xs uppercase tracking-[0.2em] font-bold text-black relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-black transition-[width] duration-300 group-hover:w-full" />
              </a>
            ))}
          </nav>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ y: "-100%" }}
            animate={{ y: 0 }}
            exit={{ y: "-100%" }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] bg-black text-white flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="px-5 h-28 flex items-center justify-between border-b border-white/20">
              <Logo onClick={(e) => go(e, "#home")} invert />
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-close-menu"
                aria-label="Fermer"
              >
                <X size={28} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col justify-center px-8 gap-2">
              {links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.15 + i * 0.08 }}
                  className="font-display font-black uppercase text-5xl tracking-tight py-2"
                  data-testid={`mobile-link-${l.href.replace("#", "")}`}
                >
                  {l.label}
                </motion.a>
              ))}
            </nav>
            <div className="px-8 pb-10">
              <LangSwitch dark />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
